import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  TextField,
  useTheme,
  FormHelperText,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { uploadFile } from 'core/s3';
import { RichTextEditor } from 'components/rich-text-editor';
import CloseIcon from '@mui/icons-material/Close';

import {
  ModalManagerProps,
  withModalManager,
} from 'components/modal-manager';
import { CUSTOM_CLASSES } from 'core/theme';
import { FlatPartnerGroup } from 'core/redux/reducers/partnerGroups';
import { Field, FieldProps, Form, Formik } from 'formik';
import * as yup from 'yup';
import { UpdateGroupFormValues } from 'types';
import { replaceNewLineWithParagraph } from 'utils/text';
import { IdentifiableChunk } from 'core/redux/reducers/types';
import { FlatUserGroup } from 'core/redux/reducers/userGroups';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppThunk } from 'core/redux';

type PartnerGroupThunk = (
  updates: Pick<FlatPartnerGroup, 'id'> & Partial<FlatPartnerGroup>,
) => AppThunk;

type UserGroupThunk = (
  updates: Pick<FlatUserGroup, 'id'> & Partial<FlatUserGroup>,
) => AppThunk;

export interface UpdateGroupDialogProps {
  group:
    | IdentifiableChunk<FlatPartnerGroup, 'id'>
    | IdentifiableChunk<FlatUserGroup, 'id'>;
  updateThunk: UserGroupThunk | PartnerGroupThunk;
  id: string;
}

const validationSchema = yup.object({
  name: yup.string().required('Field is required'),
  description: yup.string().required('Field is required'),
  isPublic: yup.boolean(),
});

export const UpdateGroupDialog: React.FC<
  UpdateGroupDialogProps & ModalManagerProps
> = ({ isOpen, close, clear, group, updateThunk, id }) => {
  const theme = useTheme();
  const [isSubmitting, setSubmitionState] = useState(false);
  const onClose = useCallback(() => close(), [close]);

  const dispatch = useDispatch<AppDispatch>();

  const handleSubmit = useCallback(
    async (values: UpdateGroupFormValues) => {
      if (
        group.name === values.name &&
        group.description === values.description &&
        'isPublic' in group &&
        group?.isPublic === values.isPublic
      ) {
        close();
        return;
      }

      setSubmitionState(true);
      await dispatch(
        updateThunk({
          id,
          ...values,
        }),
      );
      close();
    },
    [setSubmitionState, dispatch, updateThunk, id, group, close],
  );

  const s3Client = useMemo(() => {
    return {
      uploadFile,
    };
  }, []);

  return (
    <Dialog
      className={CUSTOM_CLASSES.DRAWER}
      open={isOpen}
      onClose={onClose}
      closeAfterTransition
      transitionDuration={400}
      TransitionComponent={Slide}
      TransitionProps={
        {
          direction: 'left',
          onExited: clear,
          mountOnEnter: true,
          unmountOnExit: true,
        } as TransitionProps
      }
    >
      <DialogTitle>
        Update group
        <IconButton onClick={onClose} color="inherit">
          <CloseIcon color="inherit" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Formik<UpdateGroupFormValues>
          initialValues={{
            name: group.name || '',
            description: group.description || '',
            isPublic: 'isPublic' in group ? group?.isPublic : false,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <Form
            id="update-group-form"
            style={{
              display: 'flex',
              flexDirection: 'column',
              rowGap: theme.spacing(2),
              padding: `${theme.spacing(2)} 0`,
            }}
            role="form"
          >
            <Field name="name">
              {({ meta, field }: FieldProps<string>) => (
                <TextField
                  id="name"
                  label="Name"
                  {...field}
                  error={Boolean(meta.touched && meta.error)}
                  helperText={
                    meta.touched && meta.error ? meta.error : undefined
                  }
                />
              )}
            </Field>
            <Field name="description">
              {({ meta, field, form }: FieldProps<string>) => (
                <>
                  <Typography variant="subtitle2">Description</Typography>
                  <RichTextEditor
                    content={replaceNewLineWithParagraph(field.value)}
                    onUpdate={({ editor }) => {
                      const value = editor.getHTML();

                      form.setFieldValue(field.name, value);
                    }}
                    s3Client={s3Client}
                  />
                  {meta.touched && meta.error && (
                    <FormHelperText error={!!meta.error}>
                      {meta.error}
                    </FormHelperText>
                  )}
                </>
              )}
            </Field>
            {'isPublic' in group && (
              <Field name="isPublic">
                {({ field, form }: FieldProps<string>) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(_, value) => {
                          form.setFieldValue(field.name, value);
                        }}
                      />
                    }
                    label="Is public?"
                  />
                )}
              </Field>
            )}
          </Form>
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button
          size="large"
          variant="contained"
          color="primary"
          data-cy="submit-update-group-dialog"
          type="submit"
          form="update-group-form"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withModalManager<UpdateGroupDialogProps>()(
  UpdateGroupDialog,
);
